import "./SpaDetailsPage.css"
import React, {useEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {useParams, useNavigate} from 'react-router-dom';
import {AppDispatch} from "../../redux/store";
import RootState from "../../redux/states/root.state";
import Header from "../../components/common/Header/Header";
import SpaCard from "./components/SpaCard";
import DetailsGrid from "./components/DetailsGrid";
import {
    AlertIcon,
    BoltLineIcon,
    ChartIcon,
    ChevronDownIcon,
    ChevronRightIcon,
    DownloadLineIcon,
    FlagLineIcon,
    MessageIcon,
    PencilIcon,
    RefreshIcon,
    SlidersIcon,
    PlusIcon,
    TerminalLineIcon,
} from "../../components/common/Icons/Icons";
import {fetchSpa, flagSpa, fetchFieldHistorySummary} from "../../redux/actions/spa.actions";
import useMQTT from "../../hooks/useMQTT";
import { isV1Spa } from "../../hooks/useSpaCommand";
import useV1Snapshot from "../../hooks/useV1Snapshot";
import useV1Clock from "../../hooks/useV1Clock";
import { formatClock } from "../../services/mqtt/v1/v1Clock";
import SetSpaClockModal from "./components/SetSpaClockModal";
import NetworkCard from "./components/NetworkCard";
import {
    Fade,
    Menu,
    MenuItem,
    Popover,
    Tooltip,
    Snackbar,
    Alert,
} from "@mui/material";
import RegisterUserModal from "./components/RegisterUserModal";
import EditSerialModal from "./components/EditSerialModal";
import NotFound from "../General/NotFound";
import {SpaErrorLabels} from "../../mqttData/errors/spaErrorLabels.enums";
import {SpaStatusLabels} from "../../mqttData/status/spaStatusLabels.enums";
import {formatErrorCode} from "./formatCodes";
import ConfirmFirmwareModal from "../../components/common/Modals/ConfirmFirmwareModal";
import SingleUpdateModal from "../../components/common/Modals/SingleUpdateModal";
import FirmwareUpdateModal from "../../components/common/Modals/FirmwareUpdateModal";
import useV1UpdateProgress from "../../hooks/useV1UpdateProgress";
import { V1FirmwareChannel } from "../../services/api/v1Firmware.api";
import Command from "../../classes/command.class";
import { ArcticLabels } from "../SpaErrors/components/arcticLabels.enums";
import { commandKeys } from "../../mqttData/commands/commands.enum";
import EditNicknameModal from "./components/EditNicknameModal";
import {resetLiveData} from "../../redux/reducers/live.reducer";
import {resetSettingsData} from "../../redux/reducers/settings.reducer";
import {resetUpdateStatusData} from "../../redux/reducers/updateStatus.reducer";
import {resetErrorData} from "../../redux/reducers/error.reducer";
import {resetConstData} from "../../redux/reducers/const.reducer";
import usePermissionCheck from "../../hooks/usePermissionCheck";
import useRoleCheck from "../../hooks/useRoleCheck";
import EditSpaModelModal from "./components/EditSpaModelModal";
import AddSpaCommandsModal from "../Spas/components/AddSpaCommandsModal";
import ConfirmLogsModal from "../../components/common/Modals/ConfirmLogsModal";
import RequestLogsModal from "../../components/common/Modals/RequestLogsModal";
import { palName, localizePal } from "../../utils/brand";

const SpaDetailsPage: React.FC = () => {
    // Data
    const {id} = useParams();
    const navigate = useNavigate();
    const dispatch: AppDispatch = useDispatch();
    const {data: spa, loading: isLoading, error} = useSelector((state: RootState) => state.spa);
    const {canAccess: canAssignModels} = usePermissionCheck(['update_spa_tub_model']);
    const {canAccess:canRequestLogs} = usePermissionCheck(['request_spa_logs']) ;

    // Get the current connection status
    const isConnected = useSelector((state: RootState) => 
        state.spa?.currentSpaId === id && state.spa?.isCurrentSpaConnected
    );
    const isConnecting = useSelector((state: RootState) => 
        state.spa?.currentSpaId === id && state.spa?.isConnecting
    );
    const [editMode, setEdit] = useState(false);
    const [newSettings, setNewSettings] = useState({});

    // Set modals
    const [openUserRegister, setOpenUserRegister] = useState(false);
    const [openEditSerial, setOpenEditSerial] = useState(false);
    const [openEditNickname, setOpenEditNickname] = useState(false);
    const [confirmUpdate, setConfirmUpdate] = useState(false);
    const [singleUpdateOpen, setSingleUpdateOpen] = useState(false);
    // V1 pushes go through the legacy SpaServer and answer like a bulk update (one spa).
    const [v1UpdateOpen, setV1UpdateOpen] = useState(false);
    const [v1Channel, setV1Channel] = useState<V1FirmwareChannel>('public');
    const [openEditModel, setOpenEditModel] = useState(false);
    const [addCommandsOpen, setAddCommandsOpen] = useState(false);
    const [confirmLogs, setConfirmLogs] = useState(false);
    const [requestLogsOpen, setRequestLogsOpen] = useState(false);
    const {canAccess: canAddCommands} = usePermissionCheck(['assign_spa_api_commands']);
    const {canAccess: canChangeUser} = usePermissionCheck(['register_to_user','deregister_from_user']);

    // Status chips (firmware / errors) open small popovers with the detail.
    const [statusAnchor, setStatusAnchor] = useState<{ el: HTMLElement; kind: 'firmware' | 'errors' } | null>(null);

    const [firmware, setFirmware] = useState("");
    const [version, setVersion] = useState("");

    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    
    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
      setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
      setAnchorEl(null);
    };
    
    const handleSnackbarClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
      if (reason === 'clickaway') {
        return;
      }
      setSnackbarOpen(false);
    };  

    useEffect(() => {
        if (id) {
            dispatch(fetchSpa(id));
            dispatch(fetchFieldHistorySummary(id));
        }
    }, [dispatch, id]);

    // Spa data and settings:
    const {data:liveData} = useSelector((state: RootState) => state.live);
    const {data:settingsData} = useSelector((state: RootState) => state.settings);
    const {data:errorsData} = useSelector((state: RootState) => state.error);
    const {canAccess: canViewAllErrors} = usePermissionCheck(['view_all_spa_errors']);
    const {canAccess: canUpdateFirmware} = usePermissionCheck(['can_upgrade_firmware']);

    const filteredErrors = canViewAllErrors ? Object.values(SpaErrorLabels).filter(
        (code) => errorsData[code]
      ) : Object.values(SpaErrorLabels).filter(
        (code) => errorsData[code] && ArcticLabels[code] !== ""
      );

    const {data:updateData} = useSelector((state: RootState) => state.updateStatus);
    
    useEffect(() => {
        if (liveData && liveData?.spa_guid && liveData.spa_guid!==id) {
            dispatch(resetLiveData());
            dispatch(resetSettingsData());
            dispatch(resetUpdateStatusData());
            dispatch(resetErrorData());
            dispatch(resetConstData());
        }
    }, [dispatch, id, liveData]);

    
    // MQTT Functionality:
    // Neither source starts until this spa's record says which generation it is. V1 spas
    // never report to AWS IoT, so their page reads the legacy database instead.
    const isThisSpa = !!id && typeof spa?.id === 'string' && spa.id.toLowerCase() === id.toLowerCase();
    const isV1 = isThisSpa && isV1Spa(spa);
    const [commandTopic] = useMQTT(isThisSpa && !isV1 ? spa.id : "");
    const v1Snapshot = useV1Snapshot(isV1 ? spa.id : "");
    // Firmware progress comes over the legacy broker; listen only for people who can update.
    const v1Progress = useV1UpdateProgress(isV1 && canUpdateFirmware ? spa.id : "");
    const v1Updating = Boolean(v1Progress && !v1Progress.done);
    const v1ProgressText = !v1Progress ? ""
        : v1Progress.done ? (v1Progress.success ? "Firmware update complete" : "Firmware update failed")
        : v1Progress.progress < 0 ? "Starting update..."
        : `Updating firmware | Progress: ${Math.round(v1Progress.progress)}%`
            + (v1Progress.queue ? ` (${v1Progress.queue} queued)` : "");
    const v1Clock = useV1Clock(isV1 ? spa.id : "");
    const [openSetClock, setOpenSetClock] = useState(false);
    const command = new Command(commandTopic);

    if (error === "spa not found") {
        return <NotFound/>
    }

    // Update firmware information:
    const getUpdateInfo = () => {
        if (updateData.spa_id === undefined || updateData.spa_id !== id) {
            return "";
        } else if (updateData.message === 'rejected') {
            return "Update Rejected. Please try again in 5-10 minutes.";
        } else if (updateData.message === 'busy') {
            return "LPC is busy. Update will begin automatically in 2 minutes."
        }
        return `${updateData.type} version: ${updateData.version} | Progress: ${updateData.progress}%`;
    }

    const shouldShowProgress = () => {
        return (updateData.spa_id !== undefined && updateData.spa_id === id)&&(updateData.message !== 'rejected' && updateData.message !== 'busy');
    }

    const handleCancel = () => {
        setNewSettings({});
        setEdit(false);
    }

    const handleSave = () => {
        command.updateSettings({
            ...newSettings,
        });

        setNewSettings({});
        setEdit(false);
    }

    const handleReset = (value:number) => {
        const resetTypes: { [key: number]: string } = {
            0: 'All',
            1: 'Yocto',
            2: 'LPC',
            3: palName
        };
        const resetType = resetTypes[value] || 'Unknown';
        command.executeSingle(commandKeys.Reset, value);
        setSnackbarMessage(`Reset command sent for ${resetType}`);
        setSnackbarOpen(true);
        handleClose(); // Close the menu
    }

    const handleBoost = (value:number) => {
        command.executeSingle(commandKeys.PHboost, value);
    }

    const handleFlag = async () => {
        if (id) {
            await dispatch(flagSpa(id));
        }
    }
    
    // Spa clock: UTC, plus local time in the spa's own timezone when known.
    const spaTime = (() => {
        if (!liveData.timestamp) return null;
        const d = new Date(liveData.timestamp);
        const utc = d.toUTCString();
        const tz = settingsData?.timezone;
        if (!tz) return { utc, local: null as string | null, short: utc };
        try {
            const parts = new Intl.DateTimeFormat('en-GB', {
                timeZone: tz,
                weekday: 'short',
                day: '2-digit',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hour12: false,
            }).formatToParts(d);
            const get = (type: string) => parts.find(p => p.type === type)?.value ?? '';
            const tzParts = new Intl.DateTimeFormat('en-US', {
                timeZone: tz,
                timeZoneName: 'short',
            }).formatToParts(d);
            const tzAbbr = tzParts.find(p => p.type === 'timeZoneName')?.value ?? tz;
            const local = `${get('weekday')}, ${get('day')} ${get('month')} ${get('year')} ${get('hour')}:${get('minute')}:${get('second')} ${tzAbbr}`;
            const short = `${get('hour')}:${get('minute')} ${tzAbbr}`;
            return { utc, local, short };
        } catch {
            return { utc, local: null, short: utc };
        }
    })();

    const errorCount = isV1 ? v1Snapshot.errors.length : filteredErrors.length;
    const isOnline = isV1 ? v1Snapshot.connected : Boolean(isConnected && spa?.is_moved);
    const openStatus = (kind: 'firmware' | 'errors') => (e: React.MouseEvent<HTMLElement>) =>
        setStatusAnchor({ el: e.currentTarget, kind });
    const updating = shouldShowProgress() && (updateData.progress ?? 0) < 100;
    const openInNewTab = (path: string) => window.open(path, '_blank');

    const tierMapDisplay: { [key: string]: string } = {
        'P': 'Prestige',
        'S': 'Signature',
        'L': 'Legend',
        'LS': 'Legend Select'
    };

    return (
        <div className="page sd-page">
            <Header/>
            {addCommandsOpen && canAddCommands && <AddSpaCommandsModal
                open={addCommandsOpen}
                setOpen={setAddCommandsOpen}
                selection={[spa]}
            />}

            {confirmLogs && <ConfirmLogsModal
                isOpen={confirmLogs}
                setOpen={setConfirmLogs}
                setOpenRequest={setRequestLogsOpen}
                spas={[spa]}
            />
            }

            {requestLogsOpen && <RequestLogsModal
                isOpen={requestLogsOpen}
                setOpen={setRequestLogsOpen}
                spas={[spa]}
                singleSpaConnected={isConnected}
            />}

            <ConfirmFirmwareModal
                isOpen={confirmUpdate}
                setOpen={setConfirmUpdate}
                setOpenSingle={isV1 ? setV1UpdateOpen : setSingleUpdateOpen}
                setFirmware={setFirmware}
                setVersion={setVersion}
                setChannel={setV1Channel}
                spas={[spa]}
            />

            {v1UpdateOpen && <FirmwareUpdateModal
                isOpen={v1UpdateOpen}
                setOpen={setV1UpdateOpen}
                spas={[spa]}
                firmwareType={firmware}
                version={version}
                channel={v1Channel}
            />}

            <SingleUpdateModal
                isOpen={singleUpdateOpen}
                setOpen={setSingleUpdateOpen}
                id={spa?.id}
                firmwareType={firmware}
                version={version} />

            <RegisterUserModal
                isOpen={openUserRegister}
                setOpen={setOpenUserRegister}
                user={spa?.user}
                spa={spa}
            />

            <EditSerialModal
                open={openEditSerial}
                setOpen={setOpenEditSerial}
            />

            <EditNicknameModal
                open={openEditNickname}
                setOpen={setOpenEditNickname}
            />

            <EditSpaModelModal
                open={openEditModel}
                setOpen={setOpenEditModel}
            />

            <Snackbar
                open={snackbarOpen}
                autoHideDuration={3000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={handleSnackbarClose} severity="success" sx={{ width: '100%' }}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>

            {isLoading || isConnecting || v1Snapshot.loading ? (
                <div className="sd-loading"><span className="spinner" role="progressbar" aria-label="Loading"/></div>
            ) : (
                <main className="sd-main">
                    {/* ---------- Title + actions ---------- */}
                    <header className="sd-hero">
                        <div className="sd-identity">
                            <div className="sd-title-row">
                                <span className={`sd-status ${isOnline ? 'online' : 'offline'}`} title={isOnline ? 'Online' : 'Offline'}/>
                                <h1 className="sd-title">{spa?.nick_name ?? 'n/a'}</h1>
                                <button className="sd-icon-btn" onClick={() => setOpenEditNickname(true)} aria-label="Edit nickname">
                                    <PencilIcon/>
                                </button>
                                {!spa?.is_moved && <span className="chip sd-v1">v1</span>}
                            </div>
                            <div className="sd-meta">
                                <span className="sd-meta-item">
                                    <span className="sd-meta-label">SN</span>
                                    {spa?.blue_falls_serial_number ?? spa?.arctic_serial_number ?? "N/A"}
                                    {!spa?.blue_falls_serial_number &&
                                        <button className="sd-icon-btn small" onClick={() => setOpenEditSerial(true)} aria-label="Edit serial number"><PencilIcon/></button>}
                                </span>
                                {!isV1 && <span className="sd-meta-item">
                                    <span className="sd-meta-label">Model</span>
                                    {spa?.tub_model ? `${spa?.tub_model?.name} ${tierMapDisplay[spa?.tub_model?.tier]}${spa?.tub_model?.sds ? ' + SDS' : ''}` : "None"}
                                    {canAssignModels &&
                                        <button className="sd-icon-btn small" onClick={() => setOpenEditModel(true)} aria-label="Edit model"><PencilIcon/></button>}
                                </span>}
                                <span className="sd-meta-item">
                                    <span className="sd-meta-label">Owner</span>
                                    {spa?.user ? (
                                        <>
                                            <Tooltip
                                                arrow
                                                title={(spa.user.username || spa.user.email) ? (
                                                    <>
                                                        {spa.user.username && <div>{spa.user.username}</div>}
                                                        {spa.user.email && <div>{spa.user.email}</div>}
                                                    </>
                                                ) : ''}
                                            >
                                                <a
                                                    className="sd-meta-link"
                                                    href={`/user/${spa.user.id}`}
                                                    onClick={(e) => { e.preventDefault(); navigate(`/user/${spa.user.id}`); }}
                                                >
                                                    {spa.user.name ?? spa.user.username ?? spa.user.email}
                                                </a>
                                            </Tooltip>
                                            {canChangeUser &&
                                                <button className="sd-text-btn" onClick={() => setOpenUserRegister(true)}>
                                                    <PencilIcon/> Change
                                                </button>}
                                        </>
                                    ) : canChangeUser ? (
                                        <button className="sd-owner-chip empty" onClick={() => setOpenUserRegister(true)}>
                                            <PlusIcon/> Register user
                                        </button>
                                    ) : (
                                        <span className="sd-muted">No registered user</span>
                                    )}
                                </span>
                                {spa?.dealership?.name &&
                                    <span className="sd-meta-item">
                                        <span className="sd-meta-label">Dealer</span>
                                        {spa.dealership.name}
                                        {(spa.dealership.id ?? spa.dealership_id) != null &&
                                            <span className="sd-muted">#{spa.dealership.id ?? spa.dealership_id}</span>}
                                    </span>}
                                <span className="sd-meta-item">
                                    <span className="sd-meta-label">Spa Time</span>
                                    {isV1 ? (
                                        <>
                                            {/* Read from the tub itself over the classic web broker, as classic's details page did */}
                                            {v1Clock.clock ? (
                                                <Tooltip arrow title="The spa's own clock, in whatever time zone it was set to">
                                                    <span className="sd-meta-time">
                                                        {formatClock(v1Clock.clock).full}
                                                    </span>
                                                </Tooltip>
                                            ) : v1Clock.status === 'loading' ? (
                                                <span className="sd-muted">Reading…</span>
                                            ) : v1Clock.status === 'idle' ? (
                                                <span className="sd-muted">---</span>
                                            ) : (
                                                <Tooltip arrow title="The spa is offline or did not answer.">
                                                    <span className="sd-muted">Not available</span>
                                                </Tooltip>
                                            )}
                                            {v1Clock.info && v1Clock.status !== 'loading' &&
                                                <Tooltip arrow title="Read spa time">
                                                    <button className="sd-icon-btn small" onClick={v1Clock.refresh} aria-label="Read spa time"><RefreshIcon/></button>
                                                </Tooltip>}
                                            {v1Clock.info && (v1Clock.info.can_set ? (
                                                <Tooltip arrow title="Set spa time">
                                                    <button className="sd-icon-btn small" onClick={() => setOpenSetClock(true)} aria-label="Set spa time"><PencilIcon/></button>
                                                </Tooltip>
                                            ) : (
                                                <Tooltip arrow title="Time change needs a spa firmware update (1.0.32 or newer).">
                                                    <span className="sd-muted">Can't change</span>
                                                </Tooltip>
                                            ))}
                                        </>
                                    ) : spaTime ? (
                                        <Tooltip arrow title={<>{spaTime.local && <div>{spaTime.local}</div>}<div>{spaTime.utc}</div></>}>
                                            <span className="sd-meta-time">{spaTime.short}</span>
                                        </Tooltip>
                                    ) : <span className="sd-muted">---</span>}
                                </span>
                                <span className="sd-meta-chips">
                                    <button
                                        className={`chip sd-status-chip ${errorCount > 0 ? 'bad' : ''}`}
                                        onClick={openStatus('errors')}
                                        aria-haspopup="dialog"
                                    >
                                        <AlertIcon/>
                                        {errorCount > 0 ? `${errorCount} ${errorCount === 1 ? 'error' : 'errors'}` : 'No errors'}
                                    </button>
                                </span>
                            </div>
                        </div>

                        {/* V1 / Coldfire: the control page works over the legacy broker; this page reads the legacy database, so it is not live */}
                        {!spa?.is_moved &&
                        <div className="sd-actions">
                            <div className="sd-action-group">
                                <Tooltip title="Spa control" arrow>
                                    <button className="sd-action" onClick={() => openInNewTab(`/spa-control/${spa?.id}`)} aria-label="View control">
                                        <SlidersIcon/>
                                    </button>
                                </Tooltip>
                                <Tooltip title="Report" arrow>
                                    <button className="sd-action" onClick={() => openInNewTab(`/spa/${spa?.id}/report`)} aria-label="Report spa">
                                        <ChartIcon/>
                                    </button>
                                </Tooltip>
                                <Tooltip title={`${spa?.comments_count ?? 0} comments · ${spa?.unread_count ?? 0} unread · ${spa?.unresolved_count ?? 0} unresolved`} arrow>
                                    <button className="sd-action" onClick={() => openInNewTab(`/spa/${spa?.id}/comments`)} aria-label="View comments">
                                        <MessageIcon/>
                                        {(spa?.unread_count ?? 0) > 0 && <span className="sd-badge">{spa.unread_count}</span>}
                                    </button>
                                </Tooltip>
                                <Tooltip title={spa?.is_flagged ? 'Unflag spa' : 'Flag spa'} arrow>
                                    <button className={`sd-action ${spa?.is_flagged ? 'flagged' : ''}`} onClick={handleFlag} aria-label={spa?.is_flagged ? "Unflag spa" : "Flag spa"} aria-pressed={Boolean(spa?.is_flagged)}>
                                        <FlagLineIcon filled={Boolean(spa?.is_flagged)}/>
                                    </button>
                                </Tooltip>
                            </div>
                            <div className="sd-v1-refresh">
                                <span className={`sd-v1-reported${v1Snapshot.failed ? ' bad' : ''}`}>
                                    {v1Snapshot.failed
                                        ? "Couldn't refresh this spa's data"
                                        : v1Snapshot.lastReported && `Last reported ${new Date(v1Snapshot.lastReported).toLocaleString()}`}
                                </span>
                                <button className="btn btn-sm" disabled={v1Snapshot.refreshing} onClick={v1Snapshot.refresh}>
                                    {v1Snapshot.refreshing ? 'Refreshing...' : 'Refresh'}
                                </button>
                            </div>

                            {/* Firmware: pushed through the legacy SpaServer, progress over the legacy broker */}
                            {canUpdateFirmware &&
                            <div className={`sd-firmware ${v1Updating ? 'updating' : v1Progress?.done && !v1Progress.success ? 'caution' : ''}`}>
                                <div className="sd-firmware-info">
                                    <span className="sd-firmware-status">{v1ProgressText}</span>
                                    {v1Progress && v1Progress.progress >= 0 && (
                                        <div className="sd-progress" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={v1Progress.progress}>
                                            <div className="sd-progress-bar" style={{width: `${v1Progress.done ? 100 : v1Progress.progress}%`}}/>
                                        </div>
                                    )}
                                </div>
                                <button
                                    className="btn btn-sm btn-primary"
                                    disabled={!v1Snapshot.connected || v1Updating}
                                    onClick={() => setConfirmUpdate(true)}
                                >
                                    Update
                                </button>
                            </div>}
                        </div>}

                        {!!spa?.is_moved &&
                        <div className="sd-actions">
                            <div className="sd-action-group">
                                <Tooltip title="Spa control" arrow>
                                    <button className="sd-action" onClick={() => openInNewTab(`/spa-control/${spa?.id}`)} aria-label="View control">
                                        <SlidersIcon/>
                                    </button>
                                </Tooltip>
                                <Tooltip title="Report" arrow>
                                    <button className="sd-action" onClick={() => openInNewTab(`/spa/${spa?.id}/report`)} aria-label="Report spa">
                                        <ChartIcon/>
                                    </button>
                                </Tooltip>
                                <Tooltip title="Power consumption" arrow>
                                    <button className="sd-action" onClick={() => openInNewTab(`/spa/${spa?.id}/power-consumption`)} aria-label="Power consumption">
                                        <BoltLineIcon/>
                                    </button>
                                </Tooltip>
                                <Tooltip title={`${spa?.comments_count ?? 0} comments · ${spa?.unread_count ?? 0} unread · ${spa?.unresolved_count ?? 0} unresolved`} arrow>
                                    <button className="sd-action" onClick={() => openInNewTab(`/spa/${spa?.id}/comments`)} aria-label="View comments">
                                        <MessageIcon/>
                                        {(spa?.unread_count ?? 0) > 0 && <span className="sd-badge">{spa.unread_count}</span>}
                                    </button>
                                </Tooltip>
                                <Tooltip title={spa?.is_flagged ? 'Unflag spa' : 'Flag spa'} arrow>
                                    <button className={`sd-action ${spa?.is_flagged ? 'flagged' : ''}`} onClick={handleFlag} aria-label={spa?.is_flagged ? "Unflag spa" : "Flag spa"} aria-pressed={Boolean(spa?.is_flagged)}>
                                        <FlagLineIcon filled={Boolean(spa?.is_flagged)}/>
                                    </button>
                                </Tooltip>
                            </div>

                            <div className="sd-action-group">
                                <button
                                    className="btn btn-sm sd-tool-btn"
                                    aria-controls={open ? 'sd-reset-menu' : undefined}
                                    aria-haspopup="true"
                                    aria-expanded={open ? 'true' : undefined}
                                    onClick={handleClick}
                                >
                                    <RefreshIcon/> Reset <ChevronDownIcon/>
                                </button>
                                <Menu
                                    id="sd-reset-menu"
                                    anchorEl={anchorEl}
                                    open={open}
                                    onClose={handleClose}
                                    TransitionComponent={Fade}
                                    disableScrollLock={true}
                                >
                                    <MenuItem onClick={() => handleReset(0)}>All</MenuItem>
                                    <MenuItem onClick={() => handleReset(1)}>Yocto</MenuItem>
                                    <MenuItem onClick={() => handleReset(2)}>LPC</MenuItem>
                                    <MenuItem onClick={() => handleReset(3)}>{palName}</MenuItem>
                                </Menu>

                                {canAddCommands &&
                                    <button className="btn btn-sm sd-tool-btn" onClick={() => setAddCommandsOpen(true)}><TerminalLineIcon/> Command</button>}

                                {canRequestLogs &&
                                    <button className="btn btn-sm sd-tool-btn" onClick={() => setConfirmLogs(true)}><DownloadLineIcon/> Logs</button>}
                            </div>

                            {/* Firmware: status, progress, message and update, inline */}
                            <div className={`sd-firmware ${updating ? 'updating' : updateData.message === 'rejected' || updateData.message === 'busy' ? 'caution' : ''}`}>
                                <div className="sd-firmware-info">
                                    <span className="sd-firmware-status">{getUpdateInfo()}</span>
                                    {shouldShowProgress() && (
                                        <div className="sd-progress" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={updateData.progress ?? 0}>
                                            <div className="sd-progress-bar" style={{width: `${updateData.progress ?? 0}%`}}/>
                                        </div>
                                    )}
                                    {updateData.message &&
                                        <span className="sd-firmware-msg">{updateData.message === "update_status" ? "Installing..." : updateData.message}</span>}
                                </div>
                                {canUpdateFirmware &&
                                    <button
                                        className="btn btn-sm btn-primary"
                                        disabled={!isConnected || updating}
                                        onClick={() => setConfirmUpdate(true)}
                                    >
                                        Update
                                    </button>
                                }
                            </div>

                            {/* Edit covers the Spa offsets and the details grid */}
                            <div className="sd-action-group">
                                {editMode ? (
                                    <>
                                        <button className="btn btn-sm" onClick={handleCancel}>Cancel</button>
                                        <button className="btn btn-sm btn-primary" disabled={Object.keys(newSettings).length===0} onClick={handleSave}>Save</button>
                                    </>
                                ) : (
                                    <button className="btn btn-sm btn-primary" disabled={!isConnected || isConnecting} onClick={()=>setEdit(true)}>
                                        <PencilIcon/> Edit
                                    </button>
                                )}
                            </div>
                        </div>
                        }
                    </header>

                    <Popover
                        open={Boolean(statusAnchor)}
                        anchorEl={statusAnchor?.el}
                        onClose={() => setStatusAnchor(null)}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                        slotProps={{ paper: { className: 'sd-popover' } }}
                    >
                        {statusAnchor?.kind === 'errors' && (
                            <div className="sd-popover-body">
                                <span className="sd-popover-title">Errors</span>
                                {errorCount > 0 ? (
                                    <ul className="sd-error-list">
                                        {/* V1 errors are decoded to ER codes, highest priority first */}
                                        {isV1 ? v1Snapshot.errors.map((err) =>
                                            <li key={err.code}><strong>{err.code}</strong> {err.label}</li>
                                        ) : filteredErrors.map((code) =>
                                            <li key={code}><strong>{formatErrorCode(code)}</strong> {localizePal(ArcticLabels[code])}</li>
                                        )}
                                    </ul>
                                ) : (
                                    <span className="sd-muted">No errors found.</span>
                                )}
                                {!isV1 && <a
                                    className="sd-card-link"
                                    href={`/spa/${spa?.id}/errors`}
                                    onClick={(e) => { e.preventDefault(); navigate(`/spa/${spa?.id}/errors`); }}
                                >
                                    More info <ChevronRightIcon/>
                                </a>}
                            </div>
                        )}
                    </Popover>

                    {/* Spa, details grid, Network */}
                    <div className="sd-body">
                    <SpaCard editMode={editMode} newSettings={newSettings} setNewSettings={setNewSettings} />

                    {/* ---------- Details grid ---------- */}
                    <section className="sd-section sd-grid-section">
                        <DetailsGrid editMode={editMode} newSettings={newSettings} setNewSettings={setNewSettings} handleBoost={handleBoost}/>
                    </section>

                    <NetworkCard/>
                    </div>

                    {isV1 && v1Clock.info?.can_set &&
                        <SetSpaClockModal
                            open={openSetClock}
                            setOpen={setOpenSetClock}
                            onSet={v1Clock.setClock}
                        />}
                </main>
            )}
        </div>
    );
};

export default SpaDetailsPage;
